// The /attribution build reports WHICH element was the largest paint (LCP), the biggest layout-shift source
// (CLS), or the slow-to-respond element (INP) — as a CSS selector string — so the dashboard can point at what
// to fix, not just show a score. Slightly larger than the plain build; it loads async so it's off the
// critical path.
import { onLCP, onINP, onCLS, onFCP, onTTFB } from "web-vitals/attribution"
import { ANALYTICS_EVENT_NAMES } from "./events"

// The most actionable "what caused this" selector per metric (undefined for FCP/TTFB, which have no element).
const debugTarget = (metric) => {
  const a = metric.attribution || {}
  switch (metric.name) {
    case "LCP":
      return a.element // the element that was the largest contentful paint
    case "CLS":
      return a.largestShiftTarget // the element behind the biggest layout shift
    case "INP":
      return a.interactionTarget // the element that was slow to respond
    default:
      return undefined
  }
}

// The actual DOM node behind a metric. Attribution reports a CSS-selector string, but the underlying entries
// also carry the real node — which we need to read our data-vitals-label and to derive a friendly label.
const debugNode = (metric) => {
  const a = metric.attribution || {}
  switch (metric.name) {
    case "LCP":
      return a.lcpEntry?.element || null // the largest-contentful-paint element
    case "CLS":
      return a.largestShiftSource?.node || null // the node behind the biggest layout shift
    case "INP":
      return a.interactionTargetElement || null // the element that was slow to respond
    default:
      return null
  }
}

// `data-vitals-*` attributes describe WHERE something rendered, set at different levels of the tree and
// composed by walking up from the affected element. All optional. Use the `vitalsAttrs` helper to set them.
//   data-vitals-section    page region: main | sidebar | aside | footer | hero | header | nav
//   data-vitals-component  component name, e.g. resource-list-item
//   data-vitals-variant    variant within that component, e.g. preview | card | compact
//   data-vitals-label      the specific element / media role (banner | avatar | thumbnail) or a custom name
const VITALS_DIMENSIONS = ["section", "component", "variant", "label"]

// Nearest value of each data-vitals-* dimension, found by an element-or-ancestor walk (the affected element,
// then up its parents). Coarser dimensions (section) usually sit on a container above finer ones (label).
const collectVitals = (node) => {
  const found = {}
  let el = node?.nodeType === 1 ? node : node?.parentElement
  while (el && el !== document.body) {
    for (const dim of VITALS_DIMENSIONS) {
      if (found[dim] == null) {
        const value = el.getAttribute?.(`data-vitals-${dim}`)
        if (value) found[dim] = value
      }
    }
    el = el.parentElement
  }
  return found
}

// Fallback when no data-vitals-* is set: the nearest ancestor's id / data-testid / aria-label, so unlabeled
// media still reads better than a raw CSS selector.
const nearestIdentifier = (node) => {
  const el = node?.nodeType === 1 ? node : node?.parentElement
  const named = el?.closest?.("[data-testid],[id],[aria-label]")
  if (!named) return undefined
  if (named.id) return `#${named.id}`
  const testid = named.getAttribute("data-testid")
  if (testid) return `@${testid}`
  return named.getAttribute("aria-label") || undefined
}

// A precise "where did this render" string — "section › component[variant] › label", omitting parts that
// aren't set; falls back to the nearest identifier. e.g. "main › resource-list-item[preview] › avatar".
const composeWhere = (vitals, node) => {
  const middle =
    vitals.component && vitals.variant
      ? `${vitals.component}[${vitals.variant}]`
      : vitals.component
  const parts = [vitals.section, middle, vitals.label].filter(Boolean)
  return parts.length ? parts.join(" › ") : nearestIdentifier(node)
}

/**
 * Report Core Web Vitals (LCP, INP, CLS) plus the supporting field metrics (FCP, TTFB) to Ahoy, once each
 * settles. web-vitals fires each callback with the FINAL value at the right time (LCP/CLS/INP accumulate over
 * the page's life and report on page hide / bfcache), so this is one event per metric per page load.
 *
 * Sent to Ahoy only — that's what the admin Web Vitals dashboard reads; PostHog/GA collect their own vitals.
 * The current (sanitized) path is attached by analytics.track, so the dashboard can break down by page.
 *
 * @param {(event: string, props?: object, options?: object) => void} track - analytics.track
 */
export const initWebVitals = (track) => {
  if (typeof window === "undefined") return

  // The page's Rails controller/action (from the server-rendered window config), so the dashboard can group
  // and filter vitals by page/section — matching the dimensions on server-tracked $view events. Path is
  // added by analytics.track itself (sanitized).
  const ctx = window.LoveIrish?.analytics || {}

  const report = (metric) => {
    const vitals = collectVitals(debugNode(metric))
    track(
      ANALYTICS_EVENT_NAMES.WEB_VITALS,
      {
        metric: metric.name, // "LCP" | "INP" | "CLS" | "FCP" | "TTFB"
        value: metric.value, // milliseconds (unitless for CLS)
        rating: metric.rating, // "good" | "needs-improvement" | "poor"
        metric_id: metric.id, // unique per page load, for de-duping
        navigation_type: metric.navigationType, // navigate | reload | back-forward | ...
        // Attribution: the source element (CSS selector) behind this metric, so the dashboard can break down
        // the slowest LCP / worst CLS shift by element + path. `debug_url` is the LCP resource (e.g. the hero
        // image) when the largest paint was an image.
        debug_target: debugTarget(metric),
        // `debug_where` is the composed "section › component[variant] › label" (from data-vitals-* attrs, or
        // the nearest id/testid/aria-label) — the dashboard's primary grouping. The individual dimensions
        // are also sent so the dashboard can break down by any one of them (e.g. all avatars, all hero media).
        debug_where: composeWhere(vitals, debugNode(metric)),
        debug_section: vitals.section,
        debug_component: vitals.component,
        debug_variant: vitals.variant,
        debug_label: vitals.label,
        debug_url: metric.name === "LCP" ? metric.attribution?.url : undefined,
        controller: ctx.controller,
        action: ctx.action,
      },
      { services: ["ahoy"] }
    )
  }

  onLCP(report)
  onINP(report)
  onCLS(report)
  onFCP(report)
  onTTFB(report)
}

export default initWebVitals
